import React, { useCallback, useMemo } from "react";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader } from "../../../components/ui/dialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { Input } from "../../../components/ui/input";
import { z } from "zod";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { CheckIcon, FileIcon, FilePlus2Icon, FolderOpenIcon, LinkIcon, XIcon } from "lucide-react";
import Spinner from "../../../components/ui/spinner";
import { useDropzone } from "react-dropzone";
import { useCreateProjectAttachmentsMutation } from "../../../data/mutations/create-project-attachments";
import { useProject } from "../../../data/project";
import { AttachmentReferenceType } from "tekkr-common/dist/enums";
import { useUploadAndAttachFilesMutation } from "../../../data/hooks/upload-and-attach-files";
import { useProjectAttachments } from "../../../data/project-attachments";
import { useAttachmentDisplay } from "../../../lib/attachments/attachments";
import { HttpAttachment } from "tekkr-common/dist/model/attachments";

interface Props {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    target: string[];
    title: string;
    showPickExisting?: boolean;
}

export function CreateAttachmentDialog(props: React.PropsWithChildren<Props>) {
    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            {props.children}
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{props.title}</DialogTitle>
                </DialogHeader>
                <AttachView {...props} closeDialog={() => props.onOpenChange?.(false)} />
            </DialogContent>
        </Dialog>
    );
}

function AttachView(props: Props & { closeDialog: () => void }) {
    const tabs = [
        {
            name: "URL",
            icon: <LinkIcon className={"me-2 h-4 w-4"} />,
            element: <AttachUrlView {...props} />,
        },
        {
            name: "Upload File",
            icon: <FileIcon className={"me-2 h-4 w-4"} />,
            element: <UploadFileView {...props} />,
        },
        ...(props.showPickExisting
            ? [
                  {
                      name: "Project Files",
                      icon: <FolderOpenIcon className={"me-2 h-4 w-4"} />,
                      element: <ProjectFilesView {...props} />,
                  },
              ]
            : []),
    ];
    return (
        <>
            <Tabs className={"-mt-1"} defaultValue={"0"}>
                <TabsList className={"mb-4"}>
                    {tabs.map((tab, i) => {
                        return (
                            <TabsTrigger key={i} value={i.toString()}>
                                {tab.icon}
                                {tab.name}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>
                {tabs.map((tab, i) => {
                    return (
                        <TabsContent key={i} value={i.toString()} asChild>
                            {tab.element}
                        </TabsContent>
                    );
                })}
            </Tabs>
        </>
    );
}

const urlPattern = z.string().url();
function AttachUrlView(props: Props & { closeDialog: () => void }) {
    const mutation = useCreateProjectAttachmentsMutation();
    const [url, setUrl] = React.useState("");
    const [name, setName] = React.useState("");
    const isValid = useMemo(() => {
        return {
            url: urlPattern.safeParse(url).success,
            name: name.trim().length > 0,
        };
    }, [url, name]);

    const { project } = useProject();
    const onAttach = async () => {
        await mutation.mutateAsync({
            projectId: project.id,
            target: props.target,
            attachments: [
                {
                    type: AttachmentReferenceType.URL,
                    url,
                    name: name.trim(),
                },
            ],
        });
        props.closeDialog();
    };

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex flex-col gap-2"}>
                <div className={"mt-2 flex flex-row items-center gap-2"}>
                    <div className={"text-sm font-medium"}>URL</div>
                    {url && !isValid.url && (
                        <div className={"text-sm font-medium text-destructive"}>Please enter a valid URL</div>
                    )}
                </div>
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={"Paste URL here"}></Input>
                {isValid.url && (
                    <>
                        <div className={"text-sm font-medium"}>Name</div>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={"e.g. Q4 Insights Report"}
                        ></Input>
                    </>
                )}
            </div>
            <Footer
                onAttach={onAttach}
                attachDisabled={!(isValid.url && isValid.name)}
                isFetching={mutation.isPending}
            />
        </div>
    );
}

function UploadFileView(props: Props & { closeDialog: () => void }) {
    const [files, setFiles] = React.useState<File[]>([]);
    const onDrop = useCallback((newFiles: File[]) => {
        setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const uploadMutation = useUploadAndAttachFilesMutation({
        target: props.target,
    });
    const uploadAndAttach = async () => {
        await uploadMutation.mutateAsync({ files });
        props.closeDialog();
    };

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex flex-col gap-3"}>
                <div className={"font-medium"}>Files</div>
                <div className={"flex flex-col gap-1"}>
                    {files.map((file) => {
                        const removeFile = () => {
                            setFiles((prevFiles) => prevFiles.filter((f) => f !== file));
                        };
                        return (
                            <div key={file.name} className={"flex flex-row items-center gap-2"}>
                                <FileIcon className={"me-2 h-5 w-5 shrink-0 text-muted-foreground"} />
                                <div className={"line-clamp-1 grow"}>{file.name}</div>
                                <Button variant={"ghost"} size={"sm"} onClick={removeFile}>
                                    <XIcon className={"h-5 w-5"} />
                                </Button>
                            </div>
                        );
                    })}
                </div>
                <div
                    {...getRootProps()}
                    className={cn(
                        "flex h-16 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-primary transition-all hover:bg-primary/10",
                        isDragActive && "animate-pulse border-solid bg-primary/20"
                    )}
                >
                    <input {...getInputProps()} />
                    <FilePlus2Icon className={cn("h-5 w-5 text-primary", isDragActive && "")} />
                    <p className={"text-sm font-medium text-primary"}>
                        Drag-and-drop files here, or click to select files
                    </p>
                </div>
            </div>
            <Footer
                attachButtonText={"Upload"}
                attachDisabled={files.length < 1}
                isFetching={uploadMutation.isPending}
                onAttach={uploadAndAttach}
            />
        </div>
    );
}

function Footer(props: {
    onAttach: () => void;
    attachDisabled?: boolean;
    attachButtonText?: string;
    isFetching?: boolean;
}) {
    return (
        <DialogFooter>
            <DialogClose>
                <Button disabled={props.isFetching} variant="secondary">
                    Cancel
                </Button>
            </DialogClose>
            <Button onClick={props.onAttach} disabled={props.attachDisabled || props.isFetching}>
                {props.isFetching && <Spinner className={"me-2 h-5 w-5"} />}
                {props.attachButtonText ?? "Attach"}
            </Button>
        </DialogFooter>
    );
}

function ProjectFilesView(props: Props & { closeDialog: () => void }) {
    const { uniqueAttachments, isLoading } = useProjectAttachments();
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const mutation = useCreateProjectAttachmentsMutation();
    const { project } = useProject();

    const selectedAttachment = uniqueAttachments?.find((a) => a.id === selectedId);

    const onAttach = async () => {
        if (!selectedAttachment) return;
        await mutation.mutateAsync({
            projectId: project.id,
            target: props.target,
            attachments: [
                selectedAttachment.reference.type === AttachmentReferenceType.URL
                    ? {
                          type: AttachmentReferenceType.URL,
                          url: selectedAttachment.reference.url,
                          name: selectedAttachment.name,
                      }
                    : {
                          type: AttachmentReferenceType.File,
                          fileId: selectedAttachment.reference.fileId,
                      },
            ],
        });
        props.closeDialog();
    };

    if (isLoading) {
        return (
            <div className={"flex flex-row items-center gap-2 p-4 text-sm text-muted-foreground"}>
                <Spinner className={"h-5 w-5"} /> Loading attachments...
            </div>
        );
    }

    if (!uniqueAttachments || uniqueAttachments.length === 0) {
        return (
            <div className={"flex flex-col gap-8"}>
                <div className={"rounded-md border p-4 text-center text-sm text-muted-foreground"}>
                    No existing attachments in this project.
                </div>
                <Footer onAttach={onAttach} attachDisabled={true} isFetching={false} />
            </div>
        );
    }

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex max-h-64 flex-col gap-1 overflow-y-auto"}>
                {uniqueAttachments.map((attachment) => (
                    <SelectableAttachmentItem
                        key={attachment.id}
                        attachment={attachment}
                        selected={selectedId === attachment.id}
                        onSelect={() => setSelectedId(attachment.id)}
                    />
                ))}
            </div>
            <Footer
                onAttach={onAttach}
                attachDisabled={!selectedId}
                attachButtonText={"Use Selected"}
                isFetching={mutation.isPending}
            />
        </div>
    );
}

function SelectableAttachmentItem({
    attachment,
    selected,
    onSelect,
}: {
    attachment: HttpAttachment;
    selected: boolean;
    onSelect: () => void;
}) {
    const { icon: Icon } = useAttachmentDisplay(attachment);
    return (
        <div
            onClick={onSelect}
            className={cn(
                "flex min-w-0 cursor-pointer flex-row items-center gap-3 rounded-lg border px-3 py-2.5 transition-all hover:bg-accent",
                selected ? "border-primary bg-primary/10" : "border-transparent"
            )}
        >
            <Icon className={"h-5 w-5 shrink-0 text-muted-foreground"} />
            <div className={"line-clamp-2 min-w-0 grow text-sm font-medium"}>{attachment.name}</div>
            {selected && <CheckIcon className={"h-5 w-5 shrink-0 text-primary"} />}
        </div>
    );
}
